import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';

export interface AccountAccessSharedUser {
  AccountAccessGuestClientId: number;
  GuestUserId: number;
  FirstName: string;
  MiddleName: string | null;
  LastName: string;
  EmailAddress: string;
  RelationshipId: number;
  RelationshipName: string;
  InviteStatus: string;
  IsActive: boolean;
  DateAdded: string;
  DateInactive: string | null;
  HostUserId: number;
  AdvisorGroupId: number;
  IsAcknowledged: boolean;
  DateReactivated: string | null;
  FullName: string;
  SharedAccounts: {
    WealthAccountId: number;
    Name: string;
    WealthId: number;
    Balance: number;
  }[];
}

export interface AccountAccessSharedUsersResponse {
  Records: AccountAccessSharedUser[];
  TotalLength: number;
  SummedValues: number | null;
}

export interface AccountToShare {
  AccountId: number;
  IsAsset: boolean;
  Name: string;
  AccountNumber: string;
  Balance: number;
  InstitutionName: string;
  InstitutionId: number;
  ClassificationId: number;
  ClassificationName: string;
  ClassificationSort: number;
  SubClassificationId: number;
  SubClassificationName: string | null;
  CreatedDate: string; // ISO 8601, no timezone offset
  LastUpdated: string; // ISO 8601, no timezone offset
  HasError: boolean;
  IsUpdating: boolean;
  ClosedDate: string | null;
  AccountType: number;
  Tags: Record<string, string>; // e.g. { "investor:996": "Legacy" }
  TagIds: number[] | null;
  Memo: string;
  UseZillow: boolean;
  MonthlyPaymentAmount: number | null;
  InitialLoanAmount: number | null;
  InterestRate: number | null;
  CanEdit: boolean;
  TransactionTypeId: number;
  TransactionTypeName: string;
  HasTaxLots: boolean;
  CanUpdateClassification: boolean;
  CanUnClose: boolean | null;
  IsHome: boolean;
  Strategy: string | null;
  AvailableBalance: number | null;
  LinkedAccountNumber: string | null;
  AccountUrl: string | null;
}

export interface AccountsToShareResponse {
  Records: AccountToShare[];
  TotalLength: number;
}

type InviteStatus = 'Pending' | 'Active' | 'Inactive' | 'Declined';
export interface AccountAccessRequest {
  AccountAccessGuestClientId: number;
  FullName: string;
  EmailAddress: string;
  InviteStatus: InviteStatus;
  DateAdded: string;
}

export interface AccountAccessRequestsResponse {
  Records: AccountAccessRequest[];
  TotalLength: number;
}

export interface AccountAccessRelationship {
  RelationshipId: number;
  RelationshipName: string;
}

export interface AccountAccessRelationshipsResponse {
  Records: AccountAccessRelationship[];
}

export interface AddSharedAccountsArgs {
  AccountAccessGuestClientId: number;
  WealthAccountIds: number[];
}

export interface AccountAccessDisclosure {
  Description: string;
}

export interface AccountAccessEmailValidation {
  UserId: number;
  IsExisting: boolean;
}

export interface EmailValidationArgs {
  userId: number;
  email: string;
}

export interface AddGuestClientArgs {
  FirstName: string;
  MiddleName: string;
  LastName: string;
  RelationshipId: number;
  EmailAddress: string;
  GuestUserId: number;
  HostUserId: number;
  HostName: string;
  HostEmail: string;
}

export interface UpdateActiveStatusArgs {
  accountAccessGuestClientId: number;
  activeRequest: 'Inactive' | 'Active';
}

export interface RespondToSharingRequestArgs {
  accountAccessGuestClientId: number;
  inviteRequest: 'Accept' | 'Decline';
}

export const accountAccessApi = createApi({
  reducerPath: 'accountAccessApi',
  baseQuery: waServicesQuery,
  tagTypes: ['AccountAccessSharedUsers', 'AccountAccessRequests'],
  endpoints: (builder) => ({
    getAccountAccessSharedUsers: builder.query<
      AccountAccessSharedUsersResponse,
      number
    >({
      queryFn: async (userId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/AccountAccess/GuestClient/${String(userId)}`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as AccountAccessSharedUsersResponse };
      },

      providesTags: (_result, _err, userId) => [
        { type: 'AccountAccessSharedUsers', id: String(userId) },
      ],
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getAccountsToShare: builder.query<AccountsToShareResponse, void>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/api/investor/AccountAccess/SharedAccounts',
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as AccountsToShareResponse };
      },
    }),
    getAccountAccessSharingRequests: builder.query<
      AccountAccessRequestsResponse,
      number
    >({
      queryFn: async (userId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/AccountAccess/Requests/${String(userId)}`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as AccountAccessRequestsResponse };
      },
      providesTags: (_result, _err, userId) => [
        { type: 'AccountAccessRequests', id: String(userId) },
      ],
    }),
    respondToSharingRequest: builder.mutation<
      null,
      RespondToSharingRequestArgs
    >({
      queryFn: async (
        { accountAccessGuestClientId, inviteRequest },
        _api,
        _extra,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: '/api/investor/AccountAccess/Requests',
          method: 'POST',
          body: {
            AccountAccessGuestClientId: accountAccessGuestClientId,
            InviteRequest: inviteRequest,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: null };
      },
      invalidatesTags: ['AccountAccessRequests'],
    }),
    getAccountAccessDisclosure: builder.query<AccountAccessDisclosure, number>({
      queryFn: async (userId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/AccountAccess/SharedAccounts/Disclosure/${String(userId)}`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as AccountAccessDisclosure };
      },
    }),
    getAccountAccessRelationships: builder.query<
      AccountAccessRelationshipsResponse,
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      void
    >({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/api/investor/AccountAccess/relationships',
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as AccountAccessRelationshipsResponse };
      },
    }),
    validateAccountAccessEmail: builder.query<
      AccountAccessEmailValidation | null,
      EmailValidationArgs
    >({
      queryFn: async ({ userId, email }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/AccountAccess/EmailValidation/${String(userId)}/${encodeURIComponent(email)}`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: (result.data as AccountAccessEmailValidation | null) ?? null,
        };
      },
    }),
    addGuestClient: builder.mutation<null, AddGuestClientArgs>({
      queryFn: async (body, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/api/investor/AccountAccess/GuestClient',
          method: 'POST',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: null };
      },
      invalidatesTags: ['AccountAccessSharedUsers'],
    }),
    addSharedAccounts: builder.mutation<null, AddSharedAccountsArgs>({
      queryFn: async (body, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: '/api/investor/AccountAccess/SharedAccounts',
          method: 'POST',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: null };
      },
      invalidatesTags: ['AccountAccessSharedUsers'],
    }),
    updateActiveStatus: builder.mutation<null, UpdateActiveStatusArgs>({
      queryFn: async (
        { accountAccessGuestClientId, activeRequest },
        _api,
        _extra,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: '/api/investor/AccountAccess/GuestClient/ActiveStatus',
          method: 'POST',
          body: {
            AccountAccessGuestClientId: accountAccessGuestClientId,
            ActiveRequest: activeRequest,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: null };
      },
      invalidatesTags: ['AccountAccessSharedUsers'],
    }),
  }),
});

export const {
  useGetAccountAccessSharedUsersQuery,
  useGetAccountsToShareQuery,
  useGetAccountAccessSharingRequestsQuery,
  useGetAccountAccessDisclosureQuery,
  useGetAccountAccessRelationshipsQuery,
  useAddGuestClientMutation,
  useAddSharedAccountsMutation,
  useLazyValidateAccountAccessEmailQuery,
  useUpdateActiveStatusMutation,
  useRespondToSharingRequestMutation,
} = accountAccessApi;
